import React, { useState, useEffect } from 'react';
import { X, Loader2, Edit2, Trash2, Check, Clock, ChevronDown, ChevronUp, Paperclip, FileText, Info, Plus, Wallet, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PaymentProgress } from './PaymentProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectModal = ({ project, aiExtractedData, aiUploadedFile, onClose, onSave }) => {
  const { token, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [projectUpdates, setProjectUpdates] = useState([]);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [editingUpdateText, setEditingUpdateText] = useState('');
  const [showUpdateHistory, setShowUpdateHistory] = useState(false);
  const [projectFiles, setProjectFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [paymentMilestones, setPaymentMilestones] = useState(project?.payment_milestones || []);
  
  const isEditMode = !!project;
  const isAILead = !!aiExtractedData && !project;
  
  // Helper to convert MM/DD/YYYY to YYYY-MM-DD for date input
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };
  
  // Helper to convert YYYY-MM-DD to MM/DD/YYYY
  const formatDateForSave = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${month}/${day}/${year}`;
    }
    return '';
  };
  
  // Helper to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };
  
  // Filter countries based on search
  const filteredCountries = countries.filter(country => 
    country.field_77_raw?.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.field_82_raw?.toLowerCase().includes(countrySearch.toLowerCase())
  );
  
  // Helper to parse currency value from various formats
  const parseCurrencyValue = (val) => {
    if (!val) return '';
    if (typeof val === 'number') return val.toString();
    if (typeof val === 'string') {
      // Remove currency symbols and parse
      let cleaned = val.replace(/[€$]/g, '').trim();
      // If it's European format (dots as thousand separator), remove dots
      if (cleaned.includes('.') && !cleaned.includes(',')) {
        cleaned = cleaned.replace(/\./g, '');
      } else if (cleaned.includes(',') && cleaned.includes('.')) {
        const lastDot = cleaned.lastIndexOf('.');
        const lastComma = cleaned.lastIndexOf(',');
        if (lastComma > lastDot) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
      return cleaned;
    }
    return val;
  };

  const [formData, setFormData] = useState({
    projectId: project?.field_2_raw || aiExtractedData?.project_code || '',
    name: project?.field_5_raw || aiExtractedData?.project_name || '',
    status: project?.field_85_raw?.[0]?.id || aiExtractedData?.lead_status_id || '',
    department: project?.field_3_raw?.[0]?.id || '',
    country: project?.field_83_raw?.[0]?.id || '',
    owner: project?.field_237_raw?.[0]?.id || currentUser?.id || '',
    assistants: project?.field_57_raw?.map(a => a.id) || [],
    startDate: formatDateForInput(project?.field_8_raw?.date || project?.field_8),
    deadline: formatDateForInput(project?.field_9_raw?.date || project?.field_9 || aiExtractedData?.deadline),
    value: parseCurrencyValue(project?.field_10_raw || project?.value || aiExtractedData?.estimated_value || ''),
    currency: project?.contract_currency || 'EUR',
    probability: project?.field_11_raw || '',
    summary: project?.field_247_raw || stripHtml(project?.field_247) || aiExtractedData?.summary || '',
    contractYear: project?.field_249_raw || project?.field_249 || new Date().getFullYear().toString()
  });

  useEffect(() => {
    fetchDropdownOptions();
    
    // Fetch project updates if in edit mode
    if (project?.id) {
      fetchProjectUpdates();
    }
    
    // Initialize country search with current country name or AI extracted country
    if (project?.field_83_raw?.[0]?.identifier) {
      const countryName = project.field_83_raw[0].identifier.split(' ').slice(1).join(' ');
      setCountrySearch(countryName || '');
    } else if (aiExtractedData?.country) {
      setCountrySearch(aiExtractedData.country);
    }
  }, []);

  // Re-initialize form when project changes
  useEffect(() => {
    if (project) {
      setFormData({
        projectId: project?.field_2_raw || '',
        name: project?.field_5_raw || '',
        status: project?.field_85_raw?.[0]?.id || '',
        department: project?.field_3_raw?.[0]?.id || '',
        country: project?.field_83_raw?.[0]?.id || '',
        owner: project?.field_237_raw?.[0]?.id || '',
        assistants: project?.field_57_raw?.map(a => a.id) || [],
        startDate: formatDateForInput(project?.field_8_raw?.date || project?.field_8),
        deadline: formatDateForInput(project?.field_9_raw?.date || project?.field_9),
        value: parseCurrencyValue(project?.field_10_raw || project?.value || ''),
        currency: project?.contract_currency || 'EUR',
        probability: project?.field_11_raw || '',
        summary: project?.field_247_raw || stripHtml(project?.field_247) || '',
        contractYear: project?.field_249_raw || project?.field_249 || new Date().getFullYear().toString()
      });
      setPaymentMilestones(project?.payment_milestones || []);
      
      // Re-initialize country search
      if (project?.field_83_raw?.[0]?.identifier) {
        const countryName = project.field_83_raw[0].identifier.split(' ').slice(1).join(' ');
        setCountrySearch(countryName || '');
      }
    }
  }, [project?.id]);

  const fetchDropdownOptions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [deptRes, countryRes, usersRes, statusRes] = await Promise.all([
        axios.get(`${API}/reference-data/departments`, { headers }),
        axios.get(`${API}/reference-data/countries`, { headers }),
        axios.get(`${API}/users`, { headers, params: { rows_per_page: 1000 } }),
        axios.get(`${API}/reference-data/project-status`, { headers })
      ]);

      setDepartments(deptRes.data.records || []);
      setCountries(countryRes.data.records || []);
      setUsers(usersRes.data.records || []);
      setStatusOptions(statusRes.data.records || []);
    } catch (error) {
      console.error('Failed to fetch dropdown options:', error);
    }
  };

  const fetchProjectUpdates = async () => {
    if (!project?.id) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/projects/${project.id}/updates`, { headers });
      setProjectUpdates(response.data?.updates || []);
    } catch (error) {
      console.error('Failed to fetch project updates:', error);
      setProjectUpdates([]);
    }
  };

  const handleEditUpdate = (update) => {
    setEditingUpdate(update.id);
    setEditingUpdateText(update.text || '');
  };

  const handleSaveUpdate = async (updateId) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      await axios.put(`${API}/projects/updates/${updateId}`, 
        { update_text: editingUpdateText },
        { headers }
      );
      await fetchProjectUpdates();
      setEditingUpdate(null);
      setEditingUpdateText('');
    } catch (error) {
      console.error('Error saving update:', error);
      alert('Failed to save update');
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      await axios.delete(`${API}/projects/updates/${updateId}`, { headers });
      await fetchProjectUpdates();
    } catch (error) {
      console.error('Error deleting update:', error);
      alert('Failed to delete update');
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!project?.id) return;

    setUploadingFiles(true);
    try {
      // Upload each file separately
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        
        // Determine if it's an image or attachment
        const isImage = file.type.startsWith('image/');
        const endpoint = isImage 
          ? `${API}/projects/${project.id}/upload-image`
          : `${API}/projects/${project.id}/upload-attachment`;
        
        await axios.post(endpoint, fd, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Refresh files list
      await fetchProjectFiles();
    } catch (error) {
      console.error('Failed to upload files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    await uploadFiles(files);
    e.target.value = ''; // Reset input
  };

  // Allow pasting images from the clipboard (edit mode only) to upload them as project images
  useEffect(() => {
    if (!isEditMode || !project?.id) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = [];
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const named = file.name
              ? file
              : new File([file], `pasted-${Date.now()}.png`, { type: file.type || 'image/png' });
            imageFiles.push(named);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        uploadFiles(imageFiles);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isEditMode, project?.id]);

  const handleDeleteFile = async (fileIndex) => {
    const file = projectFiles[fileIndex];
    if (!confirm(`Delete ${file.filename}?`)) return;

    try {
      await axios.delete(`${API}/projects/${project.id}/media/${file.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Refresh files list
      await fetchProjectFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  // Fetch project files when modal opens
  useEffect(() => {
    if (project?.id) {
      fetchProjectFiles();
    }
  }, [project?.id]);

  const fetchProjectFiles = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/media`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Combine images and attachments
      const allMedia = [
        ...(response.data.images || []),
        ...(response.data.attachments || [])
      ];
      setProjectFiles(allMedia);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setProjectFiles([]);
    }
  };

  const calculateProbabilityValue = () => {
    const value = parseFloat(formData.value) || 0;
    const prob = parseFloat(formData.probability) || 0;
    return (value * prob / 100).toFixed(2);
  };

  const addMilestone = () => {
    setPaymentMilestones(prev => ([
      ...prev,
      {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `m_${Date.now()}_${prev.length}`,
        percentage: '',
        estimated_date: '',
        notes: '',
        paid: false
      }
    ]));
  };

  const updateMilestone = (id, field, value) => {
    setPaymentMilestones(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const removeMilestone = (id) => {
    setPaymentMilestones(prev => prev.filter(m => m.id !== id));
  };

  const milestonesTotal = paymentMilestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
  const milestonesPaid = paymentMilestones.filter(m => m.paid).reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);

  const handleSave = async () => {
    if (!formData.name) {
      alert('Project name is required');
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Helper to get full connection object for referenced fields
      const getConnectionObject = (collectionName, id, identifier) => {
        if (!id) return null;
        return { id, identifier: identifier || '' };
      };

      // Get display names and country code for connections
      const statusName = statusOptions.find(s => s.id === formData.status)?.field_84_raw || '';
      const deptName = departments.find(d => d.id === formData.department)?.field_72_raw || '';
      const selectedCountry = countries.find(c => c.id === formData.country);
      const countryName = selectedCountry?.field_77_raw || '';
      const countryCode = selectedCountry?.field_82_raw || ''; // Get country code (ES, DO, etc.)
      const ownerName = users.find(u => u.id === formData.owner)?.field_12_raw?.full || users.find(u => u.id === formData.owner)?.field_12 || '';
      const assistantNames = formData.assistants.map(aid => {
        const user = users.find(u => u.id === aid);
        return { id: aid, identifier: user?.field_12_raw?.full || user?.field_12 || '' };
      });

      const projectData = {
        field_2: formData.projectId,
        field_2_raw: formData.projectId,
        field_5: formData.name,
        field_5_raw: formData.name,
        field_85: formData.status ? [getConnectionObject('status', formData.status, statusName)] : [],
        field_85_raw: formData.status ? [getConnectionObject('status', formData.status, statusName)] : [],
        field_3: formData.department ? `<span class="${formData.department}" data-kn="connection-value">${deptName}</span>` : null,
        field_3_raw: formData.department ? [getConnectionObject('dept', formData.department, deptName)] : [],
        field_83: formData.country ? [getConnectionObject('country', formData.country, countryName)] : [],
        field_83_raw: formData.country ? [getConnectionObject('country', formData.country, countryName)] : [],
        field_117: countryCode, // Country code for flag display
        field_117_raw: countryCode,
        field_237: formData.owner ? [getConnectionObject('user', formData.owner, ownerName)] : [],
        field_237_raw: formData.owner ? [getConnectionObject('user', formData.owner, ownerName)] : [],
        field_57: assistantNames,
        field_57_raw: assistantNames,
        field_8: formatDateForSave(formData.startDate),
        field_8_raw: formData.startDate ? { date: formatDateForSave(formData.startDate) } : null,
        field_9: formatDateForSave(formData.deadline),
        field_9_raw: formData.deadline ? { date: formatDateForSave(formData.deadline) } : null,
        field_10: formData.value,
        field_10_raw: formData.value,
        contract_currency: formData.currency || 'EUR',
        field_11: formData.probability,
        field_11_raw: formData.probability,
        field_247: formData.summary,
        field_247_raw: formData.summary,
        field_249: formData.contractYear,
        field_249_raw: formData.contractYear,
        payment_milestones: paymentMilestones.map(m => ({
          id: m.id,
          percentage: parseFloat(m.percentage) || 0,
          estimated_date: m.estimated_date || '',
          notes: m.notes || '',
          paid: !!m.paid
        }))
      };

      let savedProjectId = null;
      
      if (isEditMode) {
        await axios.put(`${API}/projects/${project.id}`, projectData, { headers });
        savedProjectId = project.id;
      } else {
        const response = await axios.post(`${API}/projects`, projectData, { headers });
        savedProjectId = response.data.id || response.data.project?.id;
      }
      
      // If this was an AI-generated lead with an uploaded file, attach it to the project
      if (aiUploadedFile && savedProjectId) {
        try {
          const fileFormData = new FormData();
          fileFormData.append('file', aiUploadedFile);
          
          await axios.post(`${API}/projects/${savedProjectId}/upload-attachment`, fileFormData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          console.log('✅ AI document attached to project');
        } catch (fileError) {
          console.error('Failed to attach AI document:', fileError);
          // Don't fail the whole save if attachment fails
        }
      }
      
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save project:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} project. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`bg-white dark:bg-gray-800 rounded-lg ${isEditMode ? 'max-w-6xl' : 'max-w-3xl'} w-full p-6 my-8 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-white dark:bg-gray-800 pb-4 border-b border-gray-200 dark:border-gray-700 z-10">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {isAILead && (
                <span className="text-purple-600">✨</span>
              )}
              {isEditMode ? 'Edit Project' : isAILead ? 'Review AI-Generated Lead' : 'Create New Project'}
            </h3>
            {isAILead && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Review and complete the information extracted by AI
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className={`${isEditMode ? 'grid grid-cols-1 lg:grid-cols-3 gap-6' : ''}`}>
          {/* Main Form Section */}
          <div className={`${isEditMode ? 'lg:col-span-2' : ''} space-y-4`}>
          
          {/* AI Extracted Info Box */}
          {isAILead && aiExtractedData?.additional_info && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-200 mb-2">Additional Information from AI Analysis:</p>
                  <div className="space-y-2 text-blue-800 dark:text-blue-300">
                    {aiExtractedData.additional_info.location_details && (
                      <div>
                        <span className="font-medium">Location:</span> {aiExtractedData.additional_info.location_details}
                      </div>
                    )}
                    {aiExtractedData.additional_info.key_requirements && (
                      <div>
                        <span className="font-medium">Key Requirements:</span> {aiExtractedData.additional_info.key_requirements}
                      </div>
                    )}
                    {aiExtractedData.additional_info.scope_of_work && (
                      <div>
                        <span className="font-medium">Scope of Work:</span> {aiExtractedData.additional_info.scope_of_work}
                      </div>
                    )}
                    {aiExtractedData.additional_info.contact_info && (
                      <div>
                        <span className="font-medium">Contact:</span>{' '}
                        {typeof aiExtractedData.additional_info.contact_info === 'object' 
                          ? Object.entries(aiExtractedData.additional_info.contact_info)
                              .filter(([key, value]) => value)
                              .map(([key, value]) => `${key}: ${value}`)
                              .join(', ')
                          : aiExtractedData.additional_info.contact_info}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Project ID</label>
              <input
                type="text"
                value={formData.projectId}
                onChange={(e) => setFormData({...formData, projectId: e.target.value})}
                className={inputClass}
                placeholder="e.g., GE-2024-001"
              />
            </div>
            <div>
              <label className={labelClass}>Project Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className={inputClass}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className={inputClass}
              >
                <option value="">Select Status</option>
                {statusOptions.map(status => (
                  <option key={status.id} value={status.id}>
                    {status.field_84_raw}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Department</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
                className={inputClass}
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.field_72_raw || dept.field_72}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="relative">
            <label className={labelClass}>Country</label>
            <input
              type="text"
              value={countrySearch}
              onChange={(e) => {
                setCountrySearch(e.target.value);
                setShowCountryDropdown(true);
              }}
              onFocus={() => setShowCountryDropdown(true)}
              onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
              className={inputClass}
              placeholder="Search country..."
            />
            {showCountryDropdown && countrySearch && filteredCountries.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredCountries.map(country => (
                  <div
                    key={country.id}
                    onClick={() => {
                      console.log('Selected country:', country.id, country.field_77_raw);
                      setFormData({...formData, country: country.id});
                      setCountrySearch(`${country.field_82_raw} ${country.field_77_raw}`);
                      setShowCountryDropdown(false);
                    }}
                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    {country.field_82_raw} {country.field_77_raw}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Project Manager</label>
              <select
                value={formData.owner}
                onChange={(e) => setFormData({...formData, owner: e.target.value})}
                className={inputClass}
              >
                <option value="">Select Manager</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.field_12_raw?.full || user.field_12}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Team Members</label>
              <select
                multiple
                value={formData.assistants}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map(option => option.value);
                  setFormData({...formData, assistants: selected});
                }}
                className={`${inputClass} h-24`}
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.field_12_raw?.full || user.field_12}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Deadline</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Value</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  className={inputClass}
                  placeholder="0"
                  data-testid="project-value-input"
                />
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className={inputClass + ' w-24'}
                  data-testid="project-currency-select"
                >
                  <option value="EUR">EUR €</option>
                  <option value="USD">USD $</option>
                  <option value="GBP">GBP £</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Probability (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData({...formData, probability: e.target.value})}
                className={inputClass}
                placeholder="0"
              />
            </div>
            <div>
              <label className={labelClass}>Probability Value</label>
              <input
                type="text"
                value={`€ ${calculateProbabilityValue()}`}
                className={`${inputClass} bg-gray-100 dark:bg-gray-600`}
                disabled
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Contract Year</label>
              <input
                type="text"
                value={formData.contractYear}
                onChange={(e) => setFormData({...formData, contractYear: e.target.value})}
                className={inputClass}
                placeholder="2024"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Summary</label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({...formData, summary: e.target.value})}
              className={inputClass}
              rows="4"
              placeholder="Project summary..."
            />
          </div>

          {/* Payment Milestones */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-testid="payment-milestones-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Payment Milestones</h4>
              </div>
              <button
                type="button"
                onClick={addMilestone}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                data-testid="add-milestone-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Milestone
              </button>
            </div>

            {paymentMilestones.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No payment milestones yet. Add one to track payment progress.</p>
            ) : (
              <div className="space-y-3">
                {paymentMilestones.map((m, idx) => (
                  <div key={m.id} className="grid grid-cols-12 gap-2 items-start bg-gray-50 dark:bg-gray-800 rounded-lg p-3" data-testid={`milestone-row-${idx}`}>
                    <div className="col-span-3">
                      <label className="block text-xs text-gray-500 mb-1">% of payment</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={m.percentage}
                        onChange={(e) => updateMilestone(m.id, 'percentage', e.target.value)}
                        className={inputClass}
                        placeholder="0"
                        data-testid={`milestone-percentage-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs text-gray-500 mb-1">Estimated date</label>
                      <input
                        type="date"
                        value={m.estimated_date}
                        onChange={(e) => updateMilestone(m.id, 'estimated_date', e.target.value)}
                        className={inputClass}
                        data-testid={`milestone-date-${idx}`}
                      />
                    </div>
                    <div className="col-span-4">
                      <label className="block text-xs text-gray-500 mb-1">Notes</label>
                      <input
                        type="text"
                        value={m.notes}
                        onChange={(e) => updateMilestone(m.id, 'notes', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. On contract signing"
                        data-testid={`milestone-notes-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col items-center">
                      <label className="block text-xs text-gray-500 mb-1">Paid</label>
                      <div className="flex items-center gap-1 h-10">
                        <input
                          type="checkbox"
                          checked={!!m.paid}
                          onChange={(e) => updateMilestone(m.id, 'paid', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`milestone-paid-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeMilestone(m.id)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Remove milestone"
                          data-testid={`milestone-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between pt-1">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Total: <span className={`font-semibold ${Math.round(milestonesTotal) === 100 ? 'text-green-600' : 'text-gray-700 dark:text-gray-300'}`}>{Math.round(milestonesTotal)}%</span>
                    <span className="mx-2">•</span>
                    Paid: <span className="font-semibold text-blue-600">{Math.round(milestonesPaid)}%</span>
                  </div>
                  <div className="w-40">
                    <PaymentProgress milestones={paymentMilestones} variant="bar" />
                  </div>
                </div>

                {Math.round(milestonesTotal) !== 100 && paymentMilestones.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1.5" data-testid="milestone-total-warning">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    Milestone percentages add up to {Math.round(milestonesTotal)}% (should total 100%).
                  </div>
                )}
              </div>
            )}
          </div>

          {/* This section moved to right sidebar */}

          {isEditMode && projectUpdates.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <button
                onClick={() => setShowUpdateHistory(!showUpdateHistory)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition"
              >
                <Clock className="w-4 h-4" />
                Update History ({projectUpdates.length})
                {showUpdateHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {showUpdateHistory && (
                <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
                  {projectUpdates.map((update) => (
                    <div
                      key={update.id}
                      className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 group hover:bg-gray-100 dark:hover:bg-gray-750 transition"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span className="font-medium">
                              {update.date || 'No date'}
                            </span>
                            {update.time && <span>{update.time}</span>}
                            {update.author && <span>by {update.author}</span>}
                          </div>
                          {editingUpdate === update.id ? (
                            <textarea
                              value={editingUpdateText}
                              onChange={(e) => setEditingUpdateText(e.target.value)}
                              className="w-full text-sm p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 dark:text-white"
                              rows={3}
                              autoFocus
                            />
                          ) : (
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {update.text || 'No update text'}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {editingUpdate === update.id ? (
                            <>
                              <button
                                onClick={() => handleSaveUpdate(update.id)}
                                className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                title="Save"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingUpdate(null);
                                  setEditingUpdateText('');
                                }}
                                className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditUpdate(update)}
                                className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUpdate(update.id)}
                                className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>

          {/* Media Management Sidebar - Only in Edit Mode */}
          {isEditMode && (
            <div className="lg:col-span-1 space-y-4">
              <div className="sticky top-20">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Media & Attachments
                </h4>
                
                {/* Image Upload Section */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Project Images</p>
                  <input
                    type="file"
                    id="image-upload"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <label htmlFor="image-upload" className="block">
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-800 transition cursor-pointer" data-testid="image-upload-dropzone">
                      {uploadingFiles ? (
                        <Loader2 className="w-8 h-8 mx-auto text-blue-500 mb-2 animate-spin" />
                      ) : (
                        <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {uploadingFiles ? 'Uploading…' : 'Click to upload images'}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-1">or paste from clipboard (Ctrl/Cmd + V)</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">PNG, JPG up to 10MB</p>
                    </div>
                  </label>
                  
                  {/* Image Thumbnails */}
                  {projectFiles && projectFiles.filter(f => f.content_type?.startsWith('image/')).length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {projectFiles.filter(f => f.content_type?.startsWith('image/')).map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={file.url}
                            alt={file.filename}
                            className={`w-full h-20 object-cover rounded border-2 ${
                              file.is_featured ? 'border-blue-500' : 'border-gray-200'
                            }`}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition flex items-center justify-center gap-1 rounded">
                            {!file.is_featured && (
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/projects/${project.id}/media/${file.id}/set-featured`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    fetchProjectFiles();
                                  } catch (error) {
                                    console.error('Failed to set featured:', error);
                                  }
                                }}
                                className="opacity-0 group-hover:opacity-100 p-1 bg-white rounded text-xs"
                                title="Set as featured"
                              >
                                ⭐
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteFile(index)}
                              className="opacity-0 group-hover:opacity-100 p-1 bg-red-500 text-white rounded text-xs"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          {file.is_featured && (
                            <div className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                              Featured
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* File Attachments Section */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Documents</p>
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.zip"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <label htmlFor="file-upload" className="block">
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-800 transition cursor-pointer">
                      <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Click to upload files
                      </p>
                      <p className="text-[10px] text-gray-500 mt-1">PDF, Word, Excel, ZIP</p>
                    </div>
                  </label>
                  
                  {/* File List */}
                  {projectFiles && projectFiles.filter(f => !f.content_type?.startsWith('image/')).length > 0 && (
                    <div className="space-y-2 mt-3">
                      {projectFiles.filter(f => !f.content_type?.startsWith('image/')).map((file, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 group hover:border-blue-300">
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                              {file.filename}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteFile(index)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Buttons - Full Width at Bottom */}
        <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
